import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./clientsDashboard.css";
import axios from "axios";
import { serverHost } from "../../../http_common.js";
import socket from "../../../socket-io.js";
import AddNewContact from "./point_of_sales_terminal/ware_house_comps/functional_comps/AddNewContact.jsx";
import IsLoading from "../../../IsLoading";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";

import { useSelector } from "react-redux";

import {
  ArrowForward,
  ArrowForwardIos,
  Assessment,
  AutoAwesome,
  Inventory2,
  ShoppingCart,
  PointOfSale,
  AccountBalanceWallet,
  PeopleAlt,
  LocalShipping,
  WarningAmber,
  ErrorOutline,
  CheckCircle,
  AddTask,
  Discount,
  Menu,
  NotificationsActive,
  Person,
  CurrencyExchange,
  TrendingUp,
  TrendingDown,
  Warehouse,
  Storefront,
  Payments,
  ReceiptLong,
  AccountBalance,
  Settings,
  Security,
  Business,
  QrCodeScanner,
  Add,
  Refresh,
  MoreHoriz,
  Schedule,
  ShoppingBag,
  LocalOffer,
  Inventory,
  Category,
  Groups,
  AttachMoney,
  KeyboardArrowRight,
  Insights,
  Bolt,
} from "@mui/icons-material";

import AlertBar from "../../../AlertBar";

export default function ClientsDashboard({
  handleSubNavigator,
  loading,
  setLoading,
  setIsMobileNavOpen,
}) {
  const { accessType, sessionToken } = useSelector(
    (state) => state.clientFunction
  );

  const companyData = useSelector(
    (state) => state.clientFunction?.companiesDB?.clientData
  );

  const navigate = useNavigate();

  const [alert, setAlert] = useState(null);
  const [newInfo, setNewInfo] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [dashboardPayload, setDashboardPayload] = useState({});
  const [activePeriod, setActivePeriod] = useState("12M");
  const [refreshing, setRefreshing] = useState(false);
  const [contactType, setContactType] = useState("");
  const dashboardRequest = useRef(null);
  const dashboardRefreshPending = useRef(false);

  /*
  |--------------------------------------------------------------------------
  | HELPERS
  |--------------------------------------------------------------------------
  */

  const currency = (value = 0) => value===null ? "Not recorded" :
    `₦${Number(value || 0).toLocaleString("en-NG", {
      maximumFractionDigits: 0,
    })}`;

  const number = (value = 0) => value===null ? "Not recorded" :
    Number(value || 0).toLocaleString("en-NG", {
      maximumFractionDigits: 0,
    });

  const percentage = (value = 0) =>
    `${Math.max(0, Math.min(100, Number(value || 0)))}%`;

  const compact = (v = 0) => {
  const n = Number(v || 0);
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${Math.round(n / 1e3)}k`;
  return n;
};

const hour = new Date().getHours();
const greeting =
  hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
const businessName =
  companyData?.businessProfile?.basicInfo?.businessName || "your business";

  
  const getDate = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date;
  };

  /*
  |--------------------------------------------------------------------------
  | LOAD DASHBOARD
  |--------------------------------------------------------------------------
  */

  const apiGetPayload = useCallback(async (showLoader = true) => {
    if (dashboardRequest.current) {
      dashboardRefreshPending.current = true;
      return;
    }
    const request = new AbortController();
    dashboardRequest.current = request;
    if (showLoader) {
      setLoading(true);
    }

    setRefreshing(true);

    try {
      const BASE_URL = serverHost;

      const clientId = companyData?.clientId;

      if (!clientId) {
        throw new Error(
          "Unable to load dashboard. Missing client information."
        );
      }

      const { data: response } = await axios.get(
        `${BASE_URL}/companies-dashboard/${clientId}/fetch-dashboard-data/${accessType}/apiGet`,
        { signal: request.signal },
      );
      if (dashboardRequest.current !== request || request.signal.aborted) return;

      const payload = response?.dashBoardData || {};

      if (response?.status !== 201) {
        setAlert({
          message:
            response?.message ||
            "Failed to load dashboard.",
          type: "error",
        });

        return;
      }

      setDashboardPayload(payload);

      const hasProducts =
        Array.isArray(payload?.products) &&
        payload.products.length > 0;

      if (!hasProducts) {
        setAlert({
          message:
            "Your inventory is currently empty. Add items to unlock the full inventory and sales intelligence dashboard.",
          type: "info",
        });
      } else {
        setAlert((current) => current?.type === "info" &&
          current?.message === "Your inventory is currently empty. Add items to unlock the full inventory and sales intelligence dashboard."
          ? null : current);
      }
    } catch (error) {
      if (request.signal.aborted || dashboardRequest.current !== request) return;
      console.error("Dashboard Error:", error?.code || "REQUEST_FAILED");

      let message = "Something went wrong.";

      if (
        error.code === "ERR_NETWORK" ||
        error.message?.toLowerCase()?.includes("network")
      ) {
        message =
          "No internet connection. Please check your network and try again.";
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      } else if (error.message) {
        message = error.message;
      }

      setAlert({
        message,
        type: "error",
      });
    } finally {
      if (dashboardRequest.current === request) {
        dashboardRequest.current = null;
        setLoading(false);
        setRefreshing(false);
        if (dashboardRefreshPending.current && !request.signal.aborted) {
          dashboardRefreshPending.current = false;
          apiGetPayload(false);
        }
      }
    }
  }, [companyData?.clientId, accessType, setLoading]);

  useEffect(() => {
    setDashboardPayload({});
    if (companyData?.clientId) apiGetPayload();
    return () => {
      if (dashboardRequest.current) dashboardRequest.current.abort();
      dashboardRequest.current = null;
      dashboardRefreshPending.current = false;
      setLoading(false);
    };
  }, [companyData?.clientId, sessionToken, apiGetPayload, setLoading]);

  useEffect(() => {
    const clientId = companyData?.clientId;
    if (!clientId || !sessionToken) return;
    let active = true;
    let subscribed = false;
    let joining = false;
    let refreshTimer;
    let retryTimer;
    let joinVersion = 0;

    const refreshDashboard = (change) => {
      if (!active || change?.clientId !== clientId) return;
      if (refreshTimer) return;
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        if (active) apiGetPayload(false);
      }, 200);
    };

    const joinDashboard = () => {
      if (!active || !socket.connected || joining || subscribed) return;
      clearTimeout(retryTimer);
      joining = true;
      const attempt = ++joinVersion;
      // Session verification can involve database access. The previous 5s
      // timeout raised warnings even while normal dashboard HTTP data loaded.
      socket.timeout(12000).emit("joinDashboard", { clientId }, (error, response) => {
        if (!active || attempt !== joinVersion) return;
        joining = false;
        if (!error && response?.status === 200) {
          subscribed = true;
          refreshDashboard({ clientId });
          return;
        }
        subscribed = false;
        const status = response?.status;
        // Do not mask normal dashboard content with a transport warning.
        // A failed live subscription is retried and HTTP data still refreshes.
        if (status && [400, 401, 403].includes(status)) {
          console.warn("Dashboard live subscription rejected:", status);
          return;
        }
        console.warn("Dashboard live subscription delayed; retrying.", status || error?.message || "NO_ACK");
        clearTimeout(retryTimer);
        retryTimer = setTimeout(joinDashboard, 8000);
      });
    };
    const onDisconnect = () => {
      ++joinVersion;
      joining = false;
      subscribed = false;
      clearTimeout(retryTimer);
    };

    socket.on("connect", joinDashboard);
    socket.on("disconnect", onDisconnect);
    socket.on("dashboardUpdated", refreshDashboard);
    if (socket.connected) joinDashboard();
    else socket.connect();

    // Only poll while the live channel is unavailable. The dashboard must
    // remain useful even if Socket.IO is disconnected or its ACK is delayed.
    const fallbackRefresh = setInterval(() => {
      if (active && !subscribed) apiGetPayload(false);
    }, 60000);

    return () => {
      active = false;
      ++joinVersion;
      clearTimeout(refreshTimer);
      clearTimeout(retryTimer);
      clearInterval(fallbackRefresh);
      socket.off("connect", joinDashboard);
      socket.off("disconnect", onDisconnect);
      socket.off("dashboardUpdated", refreshDashboard);
      if (subscribed && socket.connected) socket.emit("leaveDashboard", { clientId });
    };
  }, [companyData?.clientId, sessionToken, apiGetPayload]);

  /*
  |--------------------------------------------------------------------------
  | RAW DATA
  |--------------------------------------------------------------------------
  */

  const products = Array.isArray(dashboardPayload?.products)
    ? dashboardPayload.products
    : [];

  const sales = Array.isArray(dashboardPayload?.sales)
    ? dashboardPayload.sales
    : [];

  const purchases = Array.isArray(
    dashboardPayload?.purchases
  )
    ? dashboardPayload.purchases
    : [];

  const contacts = Array.isArray(dashboardPayload?.contacts)
    ? dashboardPayload.contacts
    : [];

  const customers = Array.isArray(dashboardPayload?.customers)
    ? dashboardPayload.customers
    : contacts.filter((contact) => contact?.profile?.type === "Customer");

  const suppliers = Array.isArray(dashboardPayload?.suppliers)
    ? dashboardPayload.suppliers
    : contacts.filter((contact) => contact?.profile?.type === "Supplier");

  /*
  |--------------------------------------------------------------------------
  | PRODUCT / INVENTORY INTELLIGENCE
  |--------------------------------------------------------------------------
  */

  const inventoryAnalytics = useMemo(() => {
    let totalQuantity = 0;
    let missingStock=false,missingCost=false,missingRetail=false;
    let totalInventoryValue = 0;
    let totalRetailValue = 0;

    let lowStockProducts = 0;
    let outOfStockProducts = 0;
    let finishedProducts = 0;
    let incompleteProducts = 0;

    const lowStockItems = [];

    products.forEach((product) => {
      const inventories = Array.isArray(product?.inventories)
        ? product.inventories
        : [];

      const quantity = inventories.reduce(
        (sum, inventory) =>
          sum + Number(inventory?.stock?.quantity ?? inventory?.quantity ?? 0),
        0
      );

      const costPrice = Number(
        product?.pricing?.costPrice || 0
      );

      const sellingPrice = Number(
        product?.pricing?.sellingPrice || 0
      );

      if(inventories.some(inv=>inv.stock?.quantity===undefined||!Number.isFinite(Number(inv.stock.quantity))))missingStock=true;
      if(product.pricing?.costPrice===undefined||!Number.isFinite(Number(product.pricing.costPrice))||(product.pricing?.currency||"NGN")!=="NGN")missingCost=true;
      if(product.pricing?.sellingPrice===undefined||!Number.isFinite(Number(product.pricing.sellingPrice))||(product.pricing?.currency||"NGN")!=="NGN")missingRetail=true;
      totalQuantity += quantity;
      totalInventoryValue += quantity * costPrice;
      totalRetailValue += quantity * sellingPrice;

      const isFinished =
        product?.name?.trim() &&
        product?.brand?.trim() &&
        product?.pricing?.sellingPrice !== null &&
        product?.pricing?.sellingPrice !== undefined;

      if (isFinished) {
        finishedProducts++;
      } else {
        incompleteProducts++;
      }

      if (quantity <= 0) {
        outOfStockProducts++;

        lowStockItems.push({
          ...product,
          stockStatus: "OUT_OF_STOCK",
          stockQuantity: quantity,
        });
      } else if (inventories.some(inv=>{const threshold=inv.stock?.reorderLevel??inv.stock?.minLevel;return threshold!==undefined&&Number(inv.stock?.quantity)-Number(inv.stock?.reservedQuantity||0)-Number(inv.stock?.damagedQuantity||0)<Number(threshold);})) {
        lowStockProducts++;

        lowStockItems.push({
          ...product,
          stockStatus: "LOW_STOCK",
          stockQuantity: quantity,
        });
      }
    });

    const potentialProfit =
      totalRetailValue - totalInventoryValue;

    const stockHealth =
      products.length > 0
        ? Math.round(
            ((products.length -
              lowStockProducts -
              outOfStockProducts) /
              products.length) *
              100
          )
        : 0;

    const completionRate =
      products.length > 0
        ? Math.round(
            (finishedProducts / products.length) * 100
          )
        : 0;

    return {
      totalQuantity:missingStock?null:totalQuantity,
      totalInventoryValue:missingStock||missingCost?null:totalInventoryValue,
      totalRetailValue:missingStock||missingRetail?null:totalRetailValue,
      potentialProfit:missingStock||missingCost||missingRetail?null:potentialProfit,
      lowStockProducts,
      outOfStockProducts,
      finishedProducts,
      incompleteProducts,
      stockHealth: Math.max(0, stockHealth),
      completionRate,
      lowStockItems: lowStockItems.slice(0, 6),
    };
  }, [products]);

  /*
  |--------------------------------------------------------------------------
  | SALES INTELLIGENCE
  |--------------------------------------------------------------------------
  */

  const salesAnalytics=useMemo(()=>calculateDashboardSales(sales,dashboardPayload.saleReturns||[]),[sales,dashboardPayload.saleReturns]);
  /*
  |--------------------------------------------------------------------------
  | PURCHASE INTELLIGENCE
  |--------------------------------------------------------------------------
  */

  const purchaseAnalytics=useMemo(()=>({transactions:purchases.length,totalPurchase:purchases.every(record=>Number.isFinite(Number(record.totals?.totalCost))&&(record.totals?.currency||'NGN')==='NGN')?purchases.reduce((sum,record)=>sum+Number(record.totals.totalCost),0):null}),[purchases]);
  /*
  |--------------------------------------------------------------------------
  | SALES CHART
  |--------------------------------------------------------------------------
  */

  const salesChartData = useMemo(() => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const currentYear = new Date().getFullYear();

    const monthly = {};

    months.forEach((month) => {
      monthly[month] = 0;
    });

    sales.forEach((sale) => {
      const date = getDate(sale?.createdAt);

      if (!date || date.getFullYear() !== currentYear || (sale.currency||"NGN")!=="NGN") {
        return;
      }

      const month = date.toLocaleString("en-US", {
        month: "short",
      });

      const subtotal = Number(
        sale?.totals?.total ?? 0
      );

      if (monthly[month] !== undefined) {
        monthly[month] += subtotal;
      }
    });

    return months.map((month) => ({
      month,
      sales: monthly[month],
    }));
  }, [sales]);

  /*
  |--------------------------------------------------------------------------
  | DAILY SALES CHART
  |--------------------------------------------------------------------------
  */

  const dailySalesData = useMemo(() => {
    const result = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date();

      date.setDate(date.getDate() - i);

      const start = new Date(date);
      start.setHours(0, 0, 0, 0);

      const end = new Date(date);
      end.setHours(23, 59, 59, 999);

      let revenue = 0;

      sales.forEach((sale) => {
        const saleDate = getDate(
          sale?.createdAt
        );

        if (
          saleDate && (sale.currency||"NGN")==="NGN" &&
          saleDate >= start &&
          saleDate <= end
        ) {
          revenue += Number(
            sale?.totals?.total ?? 0
          );
        }
      });

      result.push({
        day: date.toLocaleDateString("en-US", {
          weekday: "short",
        }),
        revenue,
      });
    }

    return result;
  }, [sales]);

  /*
  |--------------------------------------------------------------------------
  | CATEGORY INTELLIGENCE
  |--------------------------------------------------------------------------
  */

  const categoryData = useMemo(() => {
    const categories = {};

    products.forEach((product) => {
      const category =
        product?.category?.name ||
        product?.category ||
        product?.productCategory ||
        "Uncategorized";

      if (!categories[category]) {
        categories[category] = {
          name: category,
          items: 0,
          quantity: 0,
        };
      }

      categories[category].items += 1;

      categories[category].quantity +=
        product?.inventories?.reduce(
          (sum, inventory) =>
            sum +
            Number(inventory?.stock?.quantity ?? inventory?.quantity ?? 0),
          0
        ) || 0;
    });

    return Object.values(categories)
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);
  }, [products]);

  /*
  |--------------------------------------------------------------------------
  | BUSINESS SETUP INTELLIGENCE
  |--------------------------------------------------------------------------
  */

  const setupAnalytics = useMemo(() => {
    const business =
      companyData?.businessProfile;

    const basicInfo =
      business?.basicInfo || {};

    const registration =
      business?.registration || {};

    const location =
      business?.location?.headOffice || {};

    const operational =
      business?.operationalProfile || {};

    const branding =
      business?.branding || {};

    const contact =
      business?.contact || {};

    const bankAccounts =
      Array.isArray(companyData?.bank_accounts)
        ? companyData.bank_accounts
        : [];

    const checks = [
      {
        key: "business",
        label: "Business profile",
        complete: Boolean(
          basicInfo?.businessName &&
            basicInfo?.description &&
            basicInfo?.industry?.name
        ),
      },
      {
        key: "registration",
        label: "Business registration",
        complete: Boolean(
          registration?.registrationNumber ||
            registration?.taxIdentificationNumber
        ),
      },
      {
        key: "location",
        label: "Business location",
        complete: Boolean(
          location?.state &&
            location?.city &&
            location?.address
        ),
      },
      {
        key: "operations",
        label: "Operating setup",
        complete: Boolean(
          operational?.estimatedEmployees ||
            operational?.estimatedBranches
        ),
      },
      {
        key: "branding",
        label: "Business branding",
        complete: Boolean(
          branding?.logo ||
            branding?.primaryColor
        ),
      },
      {
        key: "contact",
        label: "Business contacts",
        complete: Boolean(
          contact?.whatsapp ||
            contact?.facebook ||
            contact?.instagram ||
            contact?.linkedin
        ),
      },
      {
        key: "bank",
        label: "Bank account",
        complete: bankAccounts.some(
          (account) =>
            account?.accountNumber &&
            account?.accountName &&
            account?.bankName
        ),
      },
    ];

    const completed = checks.filter(
      (item) => item.complete
    ).length;

    const progress =
      checks.length > 0
        ? Math.round(
            (completed / checks.length) * 100
          )
        : 0;

    return {
      checks,
      completed,
      total: checks.length,
      progress,
      complete: progress === 100,
    };
  }, [companyData]);

  /*
  |--------------------------------------------------------------------------
  | BUSINESS HEALTH
  |--------------------------------------------------------------------------
  */

  const businessHealth = useMemo(() => {
    const inventoryScore =
      inventoryAnalytics.stockHealth;

    const productScore =
      inventoryAnalytics.completionRate;

    const setupScore =
      setupAnalytics.progress;

    const salesScore =
      salesAnalytics.transactions > 0
        ? Math.min(
            100,
            salesAnalytics.transactions * 10
          )
        : 0;

    const profitScore =
      salesAnalytics.totalRevenue > 0
        ? Math.max(
            0,
            Math.min(
              100,
              Math.round(
                (salesAnalytics.totalProfit /
                  salesAnalytics.totalRevenue) *
                  100
              )
            )
          )
        : 0;

    const score = Math.round(
      inventoryScore * 0.25 +
        productScore * 0.2 +
        setupScore * 0.2 +
        salesScore * 0.15 +
        profitScore * 0.2
    );

    let status = "Needs attention";

    if (score >= 85) {
      status = "Excellent";
    } else if (score >= 70) {
      status = "Healthy";
    } else if (score >= 50) {
      status = "Growing";
    }

    return {
      score,
      status,
    };
  }, [
    inventoryAnalytics,
    setupAnalytics,
    salesAnalytics,
  ]);

  /*
  |--------------------------------------------------------------------------
  | RECENT SALES
  |--------------------------------------------------------------------------
  */

  const recentSales = useMemo(() => {
    return [...sales]
      .sort((a, b) => {
        const dateA = getDate(a?.createdAt);
        const dateB = getDate(b?.createdAt);

        return (
          (dateB?.getTime() || 0) -
          (dateA?.getTime() || 0)
        );
      })
      .slice(0, 6);
  }, [sales]);

  /*
  |--------------------------------------------------------------------------
  | QUICK ACTIONS
  |--------------------------------------------------------------------------
  */

  const quickActions = [
    {
      title: "New Sale",
      description: "Open Point of Sale",
      icon: <PointOfSale />,
      onClick: () => navigate("/clients/workspace/terminal-login"),
    },
    {
      title: "Add Items",
      description: "Create inventory item",
      icon: <Inventory2 />,
      onClick: () => handleSubNavigator("add-new-item", "Add New Item", "Manage Items"),
    },
    {
      title: "Receive Stock",
      description: "Receive new inventory",
      icon: <LocalShipping />,
      onClick: () => handleSubNavigator("recieves", "Recieves", "Purchases"),
    },
    {
      title: "Add Customer",
      description: "Create customer",
      icon: <PeopleAlt />,
      onClick: () => setContactType("Customer"),
    },
    {
      title: "Add Supplier",
      description: "Create supplier",
      icon: <Storefront />,
      onClick: () => setContactType("Supplier"),
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | RADIAL DATA
  |--------------------------------------------------------------------------
  */

  const radialData = [
    {
      name: "Health",
      value: businessHealth.score,
      fill:
        businessHealth.score >= 80
          ? "#16a34a"
          : businessHealth.score >= 60
          ? "#3a84f8"
          : "#f59e0b",
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (
  <>
    {alert && <AlertBar alert={alert} setAlert={setAlert} />}
    {contactType && (
      <div
        id="modalContainer"
        role="dialog"
        aria-modal="true"
        aria-label={`Add ${contactType}`}
        onClick={() => setContactType("")}
      >
        <AddNewContact
          key={contactType}
          initialContactType={contactType}
          setOpenModal={() => setContactType("")}
          onCreated={() => apiGetPayload(false)}
        />
      </div>
    )}

    <div className="cdx">
      {/* ---------- MOBILE TOP BAR ---------- */}
      <header className="cdx-topbar">
        <div className="cdx-avatar">
          {companyData?.founder?.photo ? (
            <img src={companyData.founder.photo} alt="" />
          ) : (
            <Person />
          )}
        </div>

        <div className="cdx-topbar-actions">
          <button
            type="button"
            className="cdx-pos-pill"
            onClick={() => navigate("/clients/workspace/terminal-login")}
          >
            <PointOfSale /> Terminal
          </button>

          <button type="button" className="cdx-icon-btn" aria-label="Notifications">
            <NotificationsActive />
            <span className="cdx-dot">0</span>
          </button>

          <button
            type="button"
            className="cdx-icon-btn"
            aria-label="Open menu"
            onClick={() => setIsMobileNavOpen(true)}
          >
            <Menu />
          </button>
        </div>
      </header>

      {/* ---------- HERO ---------- */}
      <section className="cdx-hero">
        <div className="cdx-hero-main">
          <p className="cdx-hero-hello">
            {greeting}, here is how {businessName} is doing.
          </p>
          <p className="cdx-hero-label">Today's recorded gross profit</p>
          <h1 className={`cdx-hero-value ${salesAnalytics.todayProfit < 0 ? "neg" : ""}`}>
            {currency(salesAnalytics.todayProfit)}
          </h1>

          <div className="cdx-hero-meta">
            <span className="cdx-chip">
              <TrendingUp /> {currency(salesAnalytics.todayRevenue)} revenue
            </span>
            <span className="cdx-chip">
              <Assessment /> {currency(salesAnalytics.monthRevenue)} this month
            </span>
            <button
              type="button"
              className="cdx-chip"
              style={{ border: "none", cursor: "pointer" }}
              onClick={() => apiGetPayload(false)}
              disabled={refreshing}
            >
              <Refresh className={refreshing ? "cdx-spin" : ""} />
              {refreshing ? "Updating" : "Refresh"}
            </button>
          </div>
        </div>

        <div className="cdx-hero-spark">
          <p className="cdx-hero-spark-cap">Revenue, last 7 days</p>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dailySalesData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="heroGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8fb6ff" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#8fb6ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="day"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#b9c8e4", fontSize: 11 }}
              />
              <Tooltip
                formatter={(v) => [currency(v), "Revenue"]}
                contentStyle={{ borderRadius: 12, border: "none" }}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#8fb6ff"
                strokeWidth={2.5}
                fill="url(#heroGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* ---------- QUICK ACTIONS ---------- */}
      <nav className="cdx-actions" aria-label="Quick actions">
        {quickActions.map((action) => (
          <button
            key={action.title}
            type="button"
            className="cdx-action"
            onClick={action.onClick}
          >
            <span className="cdx-action-icon">{action.icon}</span>
            <span className="cdx-action-text">
              <span className="cdx-action-title">{action.title}</span>
              <span className="cdx-action-desc">{action.description}</span>
            </span>
          </button>
        ))}
      </nav>

      {/* ---------- SETUP BANNER ---------- */}
      {!setupAnalytics.complete && (
        <section className="cdx-card cdx-setup">
          <div className="cdx-title-wrap">
            <span className="cdx-badge-icon">
              <Settings />
            </span>
            <div>
              <h4 className="cdx-card-title">Complete your business setup</h4>
              <p className="cdx-card-sub">
                {setupAnalytics.completed} of {setupAnalytics.total} setup areas completed
              </p>
            </div>
          </div>

          <div className="cdx-setup-progress">
            <div
              className="cdx-bar"
              role="progressbar"
              aria-valuenow={setupAnalytics.progress}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <span style={{ width: `${setupAnalytics.progress}%` }} />
            </div>
            <strong>{setupAnalytics.progress}%</strong>
            <button
              type="button"
              className="cdx-go-btn"
              aria-label="Continue setup"
              onClick={() => navigate("/cloud-profile-account/client-account?page=company-profile")}
            >
              <ArrowForward fontSize="small" />
            </button>
          </div>
        </section>
      )}

      {/* ---------- KPI GRID ---------- */}
      <section className="cdx-kpis">
        <article className="cdx-card cdx-kpi">
          <div className="cdx-kpi-top">
            <span>Items</span>
            <span className="cdx-kpi-icon tone-blue"><Inventory2 /></span>
          </div>
          <p className="cdx-kpi-value">{number(products.length)}</p>
          <p className="cdx-kpi-foot">{number(inventoryAnalytics.totalQuantity)} units in stock</p>
        </article>

        <article className="cdx-card cdx-kpi">
          <div className="cdx-kpi-top">
            <span>Total revenue</span>
            <span className="cdx-kpi-icon tone-green"><AttachMoney /></span>
          </div>
          <p className="cdx-kpi-value">{currency(salesAnalytics.totalRevenue)}</p>
          <p className="cdx-kpi-foot">{number(salesAnalytics.soldQuantity)} units sold</p>
        </article>

        <article className="cdx-card cdx-kpi">
          <div className="cdx-kpi-top">
            <span>Recorded gross profit</span>
            <span className={`cdx-kpi-icon ${salesAnalytics.totalProfit >= 0 ? "tone-green" : "tone-red"}`}>
              {salesAnalytics.totalProfit >= 0 ? <TrendingUp /> : <TrendingDown />}
            </span>
          </div>
          <p className={`cdx-kpi-value ${salesAnalytics.totalProfit >= 0 ? "pos" : "neg"}`}>
            {currency(salesAnalytics.totalProfit)}
          </p>
          <p className="cdx-kpi-foot">{salesAnalytics.margin===null?"Margin unavailable":`${salesAnalytics.margin}% margin`}</p>
        </article>

        <article className="cdx-card cdx-kpi">
          <div className="cdx-kpi-top">
            <span>Total purchases</span>
            <span className="cdx-kpi-icon tone-amber"><ShoppingCart /></span>
          </div>
          <p className="cdx-kpi-value">{currency(purchaseAnalytics.totalPurchase)}</p>
          <p className="cdx-kpi-foot">{number(purchaseAnalytics.transactions)} transactions</p>
        </article>
      </section>

      {/* ---------- REVENUE + HEALTH ---------- */}
      <section className="cdx-row-2-1">
        <div className="cdx-card">
          <div className="cdx-card-head">
            <div>
              <h3 className="cdx-card-title">Revenue performance</h3>
              <p className="cdx-card-sub">
                {activePeriod === "7D" ? "Daily revenue, last 7 days" : "Monthly revenue this year"}
              </p>
            </div>

            <div className="cdx-segment" role="group" aria-label="Period">
              {["12M", "7D"].map((period) => (
                <button
                  key={period}
                  type="button"
                  aria-pressed={activePeriod === period}
                  onClick={() => setActivePeriod(period)}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>

          <div className="cdx-chart">
            <ResponsiveContainer width="100%" height="100%">
              {activePeriod === "7D" ? (
                <AreaChart data={dailySalesData}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3a84f8" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#3a84f8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke="#e6ebf2" strokeDasharray="4 4" />
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} width={44} tickFormatter={compact} tick={{ fill: "#94a3b8", fontSize: 12 }} />
                  <Tooltip formatter={(v) => [currency(v), "Revenue"]} contentStyle={{ borderRadius: 12, border: "1px solid #e6ebf2" }} />
                  <Area type="monotone" dataKey="revenue" stroke="#3a84f8" strokeWidth={3} fill="url(#salesGradient)" />
                </AreaChart>
              ) : (
                <BarChart data={salesChartData}>
                  <CartesianGrid horizontal vertical={false} stroke="#e6ebf2" strokeDasharray="4 4" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} width={44} tickFormatter={compact} tick={{ fill: "#94a3b8", fontSize: 12 }} />
                  <Tooltip
                    cursor={{ fill: "transparent" }}
                    formatter={(v) => [currency(v), "Revenue"]}
                    contentStyle={{ borderRadius: 12, border: "1px solid #e6ebf2" }}
                  />
                  <Bar
                    dataKey="sales"
                    radius={[8, 8, 8, 8]}
                    onMouseMove={(_, index) => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {salesChartData.map((entry, index) => (
                      <Cell key={index} fill={hoveredIndex === index ? "#2563eb" : "#cfe0fb"} />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        <div className="cdx-card">
          <div className="cdx-card-head">
            <div>
              <h3 className="cdx-card-title">Business health</h3>
              <p className="cdx-card-sub">Overall operational score</p>
            </div>
            <Insights style={{ color: "#3a84f8" }} />
          </div>

          <div className="cdx-gauge">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                data={radialData}
                cx="50%"
                cy="85%"
                innerRadius="60%"
                outerRadius="100%"
                startAngle={180}
                endAngle={0}
              >
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar background dataKey="value" cornerRadius={20} />
              </RadialBarChart>
            </ResponsiveContainer>

            <div className="cdx-gauge-center">
              <div className="cdx-gauge-score">{businessHealth.score}</div>
              <div className="cdx-gauge-status">{businessHealth.status}</div>
            </div>
          </div>

          <div className="cdx-stat-list">
            {[
              ["Inventory health", inventoryAnalytics.stockHealth],
              ["Product completion", inventoryAnalytics.completionRate],
              ["Business setup", setupAnalytics.progress],
            ].map(([label, value]) => (
              <div className="cdx-stat-row" key={label}>
                <div>
                  <span>{label}</span>
                  <strong>{value}%</strong>
                </div>
                <div className="cdx-bar plain">
                  <span style={{ width: `${value}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- INVENTORY ROW ---------- */}
      <section className="cdx-row-3">
        {/* Inventory summary */}
        <div className="cdx-card">
          <div className="cdx-card-head">
            <div className="cdx-title-wrap">
              <span className="cdx-badge-icon"><Inventory2 /></span>
              <div>
                <h4 className="cdx-card-title">Inventory</h4>
                <p className="cdx-card-sub">Current stock position</p>
              </div>
            </div>
            <button type="button" className="cdx-icon-btn" aria-label="Open inventory" onClick={() => navigate("/inventory")}>
              <ArrowForward />
            </button>
          </div>

          <div className="cdx-pair">
            <div className="cdx-mini">
              <span>Stock units</span>
              <strong>{number(inventoryAnalytics.totalQuantity)}</strong>
            </div>
            <div className="cdx-mini">
              <span>Retail value</span>
              <strong>{currency(inventoryAnalytics.totalRetailValue)}</strong>
            </div>
          </div>

          <div className="cdx-stat-row">
            <div>
              <span>Stock health</span>
              <strong>{inventoryAnalytics.stockHealth}%</strong>
            </div>
            <div className="cdx-bar plain">
              <span style={{ width: `${inventoryAnalytics.stockHealth}%` }} />
            </div>
          </div>

          <div className="cdx-alert-pill amber">
            <div><WarningAmber /> Low stock</div>
            <strong>{inventoryAnalytics.lowStockProducts}</strong>
          </div>
          <div className="cdx-alert-pill red">
            <div><ErrorOutline /> Out of stock</div>
            <strong>{inventoryAnalytics.outOfStockProducts}</strong>
          </div>
        </div>

        {/* Stock alerts */}
        <div className="cdx-card">
          <div className="cdx-card-head">
            <div>
              <h4 className="cdx-card-title">Stock alerts</h4>
              <p className="cdx-card-sub">Items that need restocking</p>
            </div>
            <WarningAmber style={{ color: "#f59e0b" }} />
          </div>

          <div className="cdx-list">
            {inventoryAnalytics.lowStockItems.length === 0 ? (
              <div className="cdx-empty">
                <CheckCircle style={{ color: "#16a34a" }} />
                <strong>Inventory looks healthy</strong>
                <span>No critical stock alerts.</span>
              </div>
            ) : (
              inventoryAnalytics.lowStockItems.map((product, index) => {
                const out = product.stockStatus === "OUT_OF_STOCK";
                return (
                  <div className="cdx-list-item" key={index}>
                    <div className="cdx-list-left">
                      <span className={`cdx-badge-icon ${out ? "tone-red" : "tone-amber"}`}>
                        <Inventory />
                      </span>
                      <div className="cdx-list-text">
                        <strong>{product?.name || "Unnamed product"}</strong>
                        <span>{out ? "Out of stock" : `${product.stockQuantity} units left`}</span>
                      </div>
                    </div>
                    <button type="button" className="cdx-icon-btn" aria-label="View in inventory" onClick={() => navigate("/inventory")}>
                      <KeyboardArrowRight />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Categories */}
        <div className="cdx-card">
          <div className="cdx-card-head">
            <div>
              <h4 className="cdx-card-title">Product categories</h4>
              <p className="cdx-card-sub">Units by category</p>
            </div>
            <Category style={{ color: "#3a84f8" }} />
          </div>

          <div>
            {categoryData.length === 0 ? (
              <div className="cdx-empty">
                <Category />
                <span>Add products with categories to see the split.</span>
              </div>
            ) : (
              categoryData.map((category, index) => {
                const max = categoryData[0]?.quantity || 1;
                const width = Math.round((category.quantity / max) * 100);
                return (
                  <div className="cdx-cat" key={index}>
                    <div>
                      <span>{category.name}</span>
                      <strong>{number(category.quantity)}</strong>
                    </div>
                    <div className="cdx-bar plain">
                      <span style={{ width: `${width}%` }} />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* ---------- RECENT TRANSACTIONS ---------- */}
      <section className="cdx-card">
        <div className="cdx-card-head">
          <div>
            <h3 className="cdx-card-title">Recent transactions</h3>
            <p className="cdx-card-sub">Latest sales activity</p>
          </div>
          <button type="button" className="cdx-link-btn" onClick={() => handleSubNavigator("sales", "Sales", "Transactions")}>
            View all <ArrowForwardIos style={{ fontSize: 13 }} />
          </button>
        </div>

        <div className="cdx-list">
          {recentSales.length === 0 ? (
            <div className="cdx-empty">
              <ReceiptLong />
              <strong>No transactions yet</strong>
              <span>Sales you make at the terminal will show up here.</span>
            </div>
          ) : (
            recentSales.map((sale, index) => {
              const amount = Number(sale?.totals?.total ?? 0);
              const date = getDate(sale?.createdAt);
              return (
                <div className="cdx-list-item" key={index}>
                  <div className="cdx-list-left">
                    <span className="cdx-badge-icon"><ReceiptLong /></span>
                    <div className="cdx-list-text">
                      <strong>
                        {sale?.invoiceNumber || sale?.transactionId || `Sale #${index + 1}`}
                      </strong>
                      <span>
                        {date
                          ? date.toLocaleString("en-NG", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "Date unavailable"}
                      </span>
                    </div>
                  </div>
                  <div className="cdx-list-right">
                    <strong>{currency(amount)}</strong>
                    <span className="cdx-status ok">Completed</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* ---------- OPERATIONS ROW ---------- */}
      <section className="cdx-row-3">
        {/* Setup checklist */}
        <div className="cdx-card">
          <div className="cdx-card-head">
            <div className="cdx-title-wrap">
              <span className="cdx-badge-icon"><Business /></span>
              <div>
                <h4 className="cdx-card-title">Business setup</h4>
                <p className="cdx-card-sub">Account readiness</p>
              </div>
            </div>
            <strong style={{ color: "#2563eb" }}>{setupAnalytics.progress}%</strong>
          </div>

          <div className="cdx-list">
            {setupAnalytics.checks.map((item, index) => (
              <div className="cdx-list-item" key={index}>
                <div className="cdx-list-left">
                  {item.complete ? (
                    <CheckCircle style={{ color: "#16a34a" }} />
                  ) : (
                    <Schedule style={{ color: "#f59e0b" }} />
                  )}
                  <span>{item.label}</span>
                </div>
                <span className={`cdx-status ${item.complete ? "ok" : "wait"}`}>
                  {item.complete ? "Ready" : "Pending"}
                </span>
              </div>
            ))}
          </div>

          {!setupAnalytics.complete && (
            <button
              type="button"
              className="cdx-primary-btn"
              onClick={() => navigate("/cloud-profile-account/client-account?page=company-profile")}
            >
              Complete business setup
            </button>
          )}
        </div>

        {/* Snapshot */}
        <div className="cdx-card">
          <div className="cdx-title-wrap">
            <span className="cdx-badge-icon"><Assessment /></span>
            <div>
              <h4 className="cdx-card-title">Business snapshot</h4>
              <p className="cdx-card-sub">Current operational footprint</p>
            </div>
          </div>

          <div className="cdx-snap">
            <div>
              <Warehouse />
              <strong>{companyData?.statistics?.warehouses || 0}</strong>
              <span>Warehouses</span>
            </div>
            <div>
              <Storefront />
              <strong>{companyData?.statistics?.branches || 0}</strong>
              <span>Branches</span>
            </div>
            <div>
              <Groups />
              <strong>{companyData?.statistics?.staff || 0}</strong>
              <span>Staff</span>
            </div>
            <div>
              <PeopleAlt />
              <strong>{customers.length}</strong>
              <span>Customers</span>
            </div>
          </div>
        </div>

        {/* Financial position */}
        <div className="cdx-card">
          <div className="cdx-title-wrap">
            <span className="cdx-badge-icon"><AccountBalanceWallet /></span>
            <div>
              <h4 className="cdx-card-title">Financial position</h4>
              <p className="cdx-card-sub">Inventory value and profit potential</p>
            </div>
          </div>

          <div className="cdx-stat-list">
            <div className="cdx-money-row">
              <span>Inventory cost</span>
              <strong>{currency(inventoryAnalytics.totalInventoryValue)}</strong>
            </div>
            <div className="cdx-money-row">
              <span>Retail value</span>
              <strong>{currency(inventoryAnalytics.totalRetailValue)}</strong>
            </div>
            <div className="cdx-money-row">
              <span>Potential inventory profit</span>
              <strong className="pos">{currency(inventoryAnalytics.potentialProfit)}</strong>
            </div>
            <div className="cdx-divider" />
            <div className="cdx-money-row">
              <span>Recorded gross profit</span>
              <strong className={salesAnalytics.totalProfit >= 0 ? "pos" : "neg"}>
                {currency(salesAnalytics.totalProfit)}
              </strong>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- CLOSING BAR ---------- */}
      <section className="cdx-card cdx-cta">
        <div className="cdx-title-wrap">
          <span className="cdx-badge-icon" style={{ background: "rgba(255,255,255,.12)", color: "#fff" }}>
            <Bolt />
          </span>
          <div>
            <strong>Your business command center is ready.</strong>
            <p className="cdx-card-sub">
              Manage inventory, sales and operations from one workspace.
            </p>
          </div>
        </div>

        <div className="cdx-cta-actions">
          <button type="button" className="cdx-btn-ghost" onClick={() => navigate("/inventory")}>
            Inventory
          </button>
          <button type="button" className="cdx-btn-solid" onClick={() => navigate("/clients/workspace/terminal-login")}>
            Launch POS
          </button>
        </div>
      </section>
    </div>
  </>
)} 

export function calculateDashboardSales(sales=[],returns=[],now=new Date()){
 const currency=record=>record.currency||'NGN',valid=value=>value!==undefined&&value!==null&&Number.isFinite(Number(value)),today=new Date(now);today.setHours(0,0,0,0);const month=new Date(now.getFullYear(),now.getMonth(),1),result={todayRevenue:0,monthRevenue:0,todayProfit:0,monthProfit:0,totalRevenue:0,totalProfit:0,totalCost:0,soldQuantity:0,transactions:sales.length,margin:0};let missingRevenue=false,missingCost=false,todayMissing=false,monthMissing=false;
 for(const sale of sales){const date=new Date(sale.createdAt),recent=date>=today&&date<=now,thisMonth=date>=month&&date<=now;if(currency(sale)!=='NGN'){missingRevenue=true;missingCost=true;continue;}const revenue=valid(sale.totals?.total)?Number(sale.totals.total):null,lines=sale.soldItems||[],known=lines.length>0&&lines.every(line=>valid(line.pricing?.costPrice)&&valid(line.soldQuantity)),cost=known?lines.reduce((sum,line)=>sum+Number(line.soldQuantity)*Number(line.pricing.costPrice),0):null,profit=revenue===null||cost===null?null:revenue-Number(sale.totals?.taxAmount||0)-cost;result.soldQuantity+=lines.reduce((sum,line)=>sum+Number(line.soldQuantity||0),0);if(revenue===null)missingRevenue=true;else{result.totalRevenue+=revenue;if(recent)result.todayRevenue+=revenue;if(thisMonth)result.monthRevenue+=revenue;}if(profit===null){missingCost=true;if(recent)todayMissing=true;if(thisMonth)monthMissing=true;}else{result.totalCost+=cost;result.totalProfit+=profit;if(recent)result.todayProfit+=profit;if(thisMonth)result.monthProfit+=profit;}}
 for(const returned of returns){const sale=sales.find(record=>record.saleId===(returned.sale_id||returned.salesId||returned.saleId)),credit=valid(returned.total_return_amount)?Number(returned.total_return_amount):null,date=new Date(returned.createdAt),recent=date>=today&&date<=now,thisMonth=date>=month&&date<=now;if(currency(returned)!=='NGN'||credit===null){missingRevenue=true;missingCost=true;continue;}result.totalRevenue-=credit;if(recent)result.todayRevenue-=credit;if(thisMonth)result.monthRevenue-=credit;const lines=returned.returned_items||[],known=sale&&valid(sale.totals?.total)&&Number(sale.totals.total)>0&&lines.every(line=>valid(sale.soldItems?.find(original=>original.sku===line.sku)?.pricing?.costPrice)),restoredCost=known?lines.reduce((sum,line)=>sum+Number(line.quantity_returned)*Number(sale.soldItems.find(original=>original.sku===line.sku).pricing.costPrice),0):null;if(restoredCost===null){missingCost=true;if(recent)todayMissing=true;if(thisMonth)monthMissing=true;}else{const netCredit=credit*(1-Number(sale.totals.taxAmount||0)/Number(sale.totals.total));result.totalCost-=restoredCost;result.totalProfit-=netCredit-restoredCost;if(recent)result.todayProfit-=netCredit-restoredCost;if(thisMonth)result.monthProfit-=netCredit-restoredCost;}}
 if(missingRevenue)result.totalRevenue=null;if(missingCost){result.totalCost=null;result.totalProfit=null;}if(todayMissing)result.todayProfit=null;if(monthMissing)result.monthProfit=null;result.margin=result.totalProfit===null||result.totalRevenue===null?null:result.totalRevenue>0?Math.round(result.totalProfit/result.totalRevenue*100):0;return result;
}
